import { useEffect, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { SearchInput } from '@aimsos/ui';
import {
  DASHBOARD_STATES,
  FILTER_BAR_BUTTON_CLASSNAME,
  FILTER_BAR_SEARCH_INPUT_CLASSNAME,
} from '../constants';
import { useDashboardData } from '../context';
import DashboardButton from './DashboardButton';
import FilterDropdown from './FilterDropdown';
import { useDashboardFilters } from '../hooks';

const CreatedFilters = () => {
  const { t } = useTranslation('dashboards');
  const {
    sectionFilters, templateCategories, fetchSectionFilters, fetchTemplateCategories,
  } = useDashboardData();
  const {
    localSearch,
    setLocalSearch,
    filters: activeFilters,
    handleStateChange,
    handleSectionChange,
    handleCategoryChange,
    resetFilters,
    hasActiveFilters,
  } = useDashboardFilters();

  useEffect(() => {
    fetchSectionFilters();
  }, [fetchSectionFilters]);

  // Same catalog `TemplateFilters` uses (`dsh dashboard categories`) — one
  // dashboard-category catalog shared by both tabs, distinct from widget categories.
  useEffect(() => {
    fetchTemplateCategories();
  }, [fetchTemplateCategories]);

  const categoryLabel = useMemo(() => {
    if (!activeFilters.categoryId) return t('filters.categories');
    const category = templateCategories.find((item) => item.id === activeFilters.categoryId);
    return category?.name || t('filters.categories');
  }, [activeFilters.categoryId, t, templateCategories]);

  const stateLabel = useMemo(() => {
    if (!activeFilters.state) return t('filters.state');
    if (activeFilters.state === DASHBOARD_STATES.DRAFT) return t('status.draft');
    if (activeFilters.state === DASHBOARD_STATES.PUBLISHED) return t('status.published');
    if (activeFilters.state === DASHBOARD_STATES.ARCHIVED) return t('status.archived');
    return t('filters.state');
  }, [activeFilters.state, t]);

  const sectionLabel = useMemo(() => {
    if (!activeFilters.sectionId) return t('filters.section');
    const section = sectionFilters.find((item) => item.id === activeFilters.sectionId);
    return section?.path || section?.name || t('filters.section');
  }, [activeFilters.sectionId, sectionFilters, t]);

  // TODO(TEMP-FALLBACK): the State options are hard-coded from the contract's
  // lifecycle enum (DRAFT / PUBLISHED / ARCHIVED) and filtered client-side,
  // because `dashboard/list` rejects `state` with DSW001 and there is no state
  // catalog endpoint (CT-5470). When the backend accepts `state` on /list or
  // exposes a catalog, take the options from it and filter server-side.
  const stateOptions = useMemo(() => [
    { value: DASHBOARD_STATES.DRAFT, label: t('status.draft') },
    { value: DASHBOARD_STATES.PUBLISHED, label: t('status.published') },
    { value: DASHBOARD_STATES.ARCHIVED, label: t('status.archived') },
  ], [t]);

  const categoryOptions = useMemo(
    () => templateCategories.map((category) => ({ value: category.id, label: category.name })),
    [templateCategories],
  );

  const sectionOptions = useMemo(
    () => sectionFilters.map((section) => ({ value: section.id, label: section.path || section.name })),
    [sectionFilters],
  );

  return (
    <section>
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-full max-w-xs">
          <SearchInput
            className={FILTER_BAR_SEARCH_INPUT_CLASSNAME}
            value={localSearch}
            onChange={(event) => setLocalSearch(event.target.value)}
            onClear={() => setLocalSearch('')}
            placeholder={t('filters.search')}
          />
        </div>

        <FilterDropdown label={stateLabel} options={stateOptions} onSelect={handleStateChange} />
        <FilterDropdown label={categoryLabel} options={categoryOptions} onSelect={handleCategoryChange} />
        <FilterDropdown
          label={sectionLabel}
          options={sectionOptions}
          onSelect={handleSectionChange}
          className="max-w-72"
        />

        {hasActiveFilters && (
          <DashboardButton variant="secondary" onClick={resetFilters} className={FILTER_BAR_BUTTON_CLASSNAME}>
            {t('filters.clear')}
          </DashboardButton>
        )}
      </div>
    </section>
  );
};

export default CreatedFilters;
